import { Link } from "react-router-dom";
import { ArrowRight, Check, X } from "lucide-react";
import { Helmet } from "react-helmet-async";

const PROD_DOMAIN = "https://www.thesecretseller.co.uk";

export default function LandingVsPhysicalStagingPage() {
  return (
    <main data-testid="landing-vs-physical" className="pt-24">
      <Helmet>
        <title>Virtual Staging vs Physical Staging | The Secret Seller</title>
        <meta name="description" content="Virtual staging costs from £55 per image vs £2,000+ for physical staging. Compare cost, speed, quality and results to decide which is right for your property." />
        <link rel="canonical" href={`${PROD_DOMAIN}/virtual-staging-vs-physical-staging`} />
      </Helmet>

      <section className="py-20 lg:py-28 bg-[#FAFAF8]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <p className="uppercase text-[11px] tracking-[0.3em] text-[#758C7B] mb-4 font-medium">Comparison</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl lg:text-[2.8rem] font-extralight text-[#0D2D54] leading-[1.1] tracking-[-0.01em] mb-8">
            Virtual staging vs physical staging
          </h1>
          <p className="text-[15px] text-[#6B7280] leading-[1.8]">Both achieve the same goal — helping buyers visualise a property. But the cost, speed and practicality are very different.</p>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-24 lg:py-32">
        <div className="max-w-4xl mx-auto px-8 lg:px-16">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-[#0D2D54] mb-10 text-center">Side by side comparison</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b-2 border-[#0D2D54]">
                  <th className="text-left py-4 pr-4 text-[#6B7280] font-medium"></th>
                  <th className="text-left py-4 px-4 text-[#0D2D54] font-medium">Virtual Staging</th>
                  <th className="text-left py-4 pl-4 text-[#0D2D54] font-medium">Physical Staging</th>
                </tr>
              </thead>
              <tbody className="text-[#6B7280]">
                {[
                  ["Cost", "From £55 per image", "£2,000 — £5,000+ per property"],
                  ["Turnaround", "48 hours", "1 — 2 weeks"],
                  ["Logistics", "Upload photos online", "Furniture delivery, setup, collection"],
                  ["Flexibility", "Multiple styles per room", "One fixed arrangement"],
                  ["Online impact", "Excellent — designed for portals", "Photos still needed separately"],
                  ["In-person impact", "Photos only", "Fully furnished for viewings"],
                  ["Ongoing cost", "None — one-time payment", "Monthly rental fees"],
                  ["Availability", "UK-wide, instant", "Location dependent"],
                ].map(([label, virtual, physical], i) => (
                  <tr key={i} className="border-b border-[#E8E8E4]">
                    <td className="py-4 pr-4 font-medium text-[#0D2D54]">{label}</td>
                    <td className="py-4 px-4">{virtual}</td>
                    <td className="py-4 pl-4">{physical}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pros and Cons */}
      <section className="py-24 lg:py-32 bg-[#FAFAF8]">
        <div className="max-w-4xl mx-auto px-8 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <div>
              <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-6">Virtual Staging</h3>
              <ul className="space-y-3 text-sm">
                {["Fraction of the cost", "48-hour delivery", "Multiple styles per room", "No logistics or coordination", "Works for any property, anywhere in the UK", "Perfect for online marketing"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#6B7280]"><Check size={14} className="text-[#758C7B] mt-0.5 flex-shrink-0" />{item}</li>
                ))}
                <li className="flex items-start gap-2 text-[#6B7280] mt-4"><X size={14} className="text-red-400 mt-0.5 flex-shrink-0" />Not visible during physical viewings</li>
              </ul>
            </div>
            <div>
              <h3 className="font-['Outfit'] text-lg font-medium text-[#0D2D54] mb-6">Physical Staging</h3>
              <ul className="space-y-3 text-sm">
                {["Real furniture for in-person viewings", "Tangible experience for buyers"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#6B7280]"><Check size={14} className="text-[#758C7B] mt-0.5 flex-shrink-0" />{item}</li>
                ))}
                {["Costs £2,000 — £5,000+ per property", "Takes 1-2 weeks to arrange", "Monthly rental fees", "Limited to one style", "Location dependent availability"].map((item, i) => (
                  <li key={i} className="flex items-start gap-2 text-[#6B7280]"><X size={14} className="text-red-400 mt-0.5 flex-shrink-0" />{item}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-[#0D2D54]">
        <div className="max-w-3xl mx-auto px-8 lg:px-16 text-center">
          <h2 className="font-['Outfit'] text-2xl lg:text-[2rem] font-extralight text-white mb-6">Virtual staging from £55 per image</h2>
          <p className="text-[15px] text-white/50 mb-10">The smart choice for modern property marketing.</p>
          <Link to="/order" className="inline-flex items-center gap-3 bg-white text-[#0D2D54] px-10 py-4 text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-white/90 transition-all">
            Send Us Your Photos <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </main>
  );
}
